import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { isSignedInAtom } from "./authAtoms";
import { useToggleWishlistMutation } from "@/api/AppApi/services/product";
import useEventTrackers from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/useEventTrackers";
import { EventType } from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/helpers/interfaces";
import { getFbcCookie, getFbpCookie } from "@/utils/functions/cookieOperations";
import { v4 as uuidv4 } from "uuid";
import { CartItemTypes } from "@/api/AppApi/services/cart/interfaces/cart-interfaces";

const FAV_KEY = "wishlist_favorites";

const readFavorites = (): number[] => {
  try {
    const raw = typeof window !== "undefined" ? localStorage.getItem(FAV_KEY) : null;
    return raw ? (JSON.parse(raw) as number[]) : [];
  } catch {
    return [];
  }
};

const writeFavorites = (ids: number[]) => {
  try {
    if (typeof window !== "undefined") localStorage.setItem(FAV_KEY, JSON.stringify(ids));
  } catch {}
};

export const useWishlist = (productId?: number, serverIsFavorite?: boolean) => {
  const [isSignedIn] = useAtom(isSignedInAtom);
  const toggleWishlist = useToggleWishlistMutation();
  const [isFavorite, setIsFavorite] = useState(false);
  const { eventTrackers } = useEventTrackers();

  useEffect(() => {
    if (!productId) return;
    const fav = isSignedIn ? !!serverIsFavorite : readFavorites().includes(productId);
    setIsFavorite(!!fav);
  }, [isSignedIn, productId, serverIsFavorite]);

  const toggle = () => {
    if (!productId) return;
    if (isSignedIn) {
      const event_id = uuidv4();
      const fbc = (getFbcCookie() as string) ?? null;
      const fbp = (getFbpCookie() as string) ?? null;
      toggleWishlist.mutate(
        { id: productId, params: { fbc, fbp, event_id } },
        {
          onSuccess: () => {
            setIsFavorite((p) => !p);
            const minimalItem: any = {
              type: CartItemTypes.product,
              inner_id: productId,
              name: "",
              discount: 0,
              id: productId,
              price: 0,
              quantity: 1,
              parent: { name: "", sku: "" },
              others: { attrs_values: [] },
              categories_names: [],
            };
            eventTrackers(
              EventType.add_to_wishlist,
              [minimalItem],
              { value: 0 },
              undefined,
              event_id,
            );
          },
        },
      );
      return;
    }
    const ids = readFavorites();
    const exists = ids.includes(productId);
    const next = exists ? ids.filter((id) => id !== productId) : [...ids, productId];
    writeFavorites(next);
    setIsFavorite(!exists);
  };

  return { isFavorite, toggle };
};

export const wishlistStorageKey = FAV_KEY;

